import { ClockIcon, CrossIcon } from 'assets/icons';
import Button from 'components/button';
import ChoiceChips from 'components/tasks/ChoiceChips';
import { TASK_TYPE } from 'constants/taskTypes';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { clockLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { longDay } from 'pages/planning/planningItems';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { clockOf, momentOf, shiftDay } from './AppointmentEditForm';

const TYPES = ['todo', 'meeting'];
const pad = (value) => String(value).padStart(2, '0');
// The cursor in the title at once, without scrolling the grid.
const focusOnMount = (element) => element?.focus({ preventScroll: true });
const fieldClass =
  'min-w-0 flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-900 outline-none transition-colors focus:border-yellow-500';
const fullName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

/**
 * The creation on a slot of the planning, as in Google Calendar: a title, the
 * type, the moment picked and a description. Entrée saves (⌘ or Ctrl + Entrée
 * in the description); Échap or a click elsewhere closes; « Plus d'options »
 * opens the whole task form. `draft`: { start, minutes, allDay, type, title,
 * note }. « Tâche » and « Rendez-vous » are two things, not two kinds of task:
 * a rendez-vous is an appointment of the planning and needs a time (the band
 * without a time only takes tasks), and has no « Plus d'options ».
 * A rendez-vous goes in my calendar or a colleague's (« Agenda de »), takes
 * the whole day or a slot, over one day or several. Its picks live in the
 * draft (meetingAllDay, ownerId, fromDay, fromTime, toDay, toTime), each one
 * falling back on the slot clicked; the days are local days, as the grid.
 */
const QuickCreate = ({ draft, onChange, onClose, onSaved, onMore }) => {
  const { text } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { call, me } = useCrmContext();
  const notifyTasks = useNotifyTasksChanged();
  const box = useRef(null);
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call]);
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    const onDown = (event) => {
      if (!box.current?.contains(event.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [onClose]);

  const title = draft.title.trim();
  const note = (draft.note ?? '').trim();
  const end = new Date(draft.start.getTime() + draft.minutes * 60000);
  const meeting = draft.type === 'meeting';
  const wholeDay = Boolean(draft.meetingAllDay);
  const fromDay = draft.fromDay ?? isoDay(draft.start);
  const fromTime = draft.fromTime ?? clockOf(draft.start);
  const toDay = draft.toDay ?? isoDay(wholeDay ? draft.start : end);
  const toTime = draft.toTime ?? clockOf(end);
  const startsAt = momentOf(fromDay, fromTime);
  const endsAt = momentOf(toDay, toTime);
  const slotOk = wholeDay ? Boolean(fromDay && toDay && toDay >= fromDay) : Boolean(startsAt && endsAt && endsAt > startsAt);
  const colleagues = team.filter((person) => person.id !== me?.id);
  // A chosen colleague is kept even if the team has not loaded (or failed):
  // the appointment must never fall back into my own calendar.
  const ownerId = draft.ownerId && draft.ownerId !== me?.id ? draft.ownerId : null;
  const owner = colleagues.find((person) => person.id === ownerId) ?? null;
  // The span is kept when the first day moves, as in Google Calendar.
  const moveFromDay = (day) => {
    if (!day) return onChange({ fromDay: day });
    const span = toDay && fromDay ? Math.round((fromIsoDay(toDay) - fromIsoDay(fromDay)) / 86400000) : 0;
    return onChange({ fromDay: day, toDay: shiftDay(day, span) });
  };

  const save = async () => {
    if (!title || busy || (meeting && !slotOk)) return;
    setBusy(true);
    try {
      if (meeting) {
        const slot = wholeDay
          ? { allDay: true, startDay: fromDay, endDay: shiftDay(toDay, 1) }
          : { allDay: false, startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() };
        const created = await call(crmApi.createAppointment, {
          ...(ownerId ? { ownerId } : {}),
          title,
          description: note,
          ...slot,
          category: 'interne',
        });
        toast.success(owner ? text('meetingAddedFor', { name: fullName(owner) }) : text('meetingAdded'));
        onSaved({ kind: 'appointment', id: created.id, start: wholeDay ? fromIsoDay(fromDay) : startsAt });
      } else {
        const created = await call(crmApi.createTask, {
          fields: {
            type: TASK_TYPE,
            title,
            note,
            dueOn: isoDay(draft.start),
            dueTime: draft.allDay ? null : `${pad(draft.start.getHours())}:${pad(draft.start.getMinutes())}`,
            durationMin: draft.minutes,
            assigneeId: me?.id,
          },
        });
        notifyTasks();
        toast.success(text('taskAdded'));
        onSaved({ kind: 'task', id: created.id, start: draft.start });
      }
    } catch (error) {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={box} className="w-[340px] rounded-2xl border border-gray-100 bg-white p-4 shadow-lg">
      <div className="flex items-start gap-2">
        <input
          ref={focusOnMount}
          value={draft.title}
          onChange={(event) => onChange({ title: event.target.value })}
          onKeyDown={(event) => event.key === 'Enter' && save()}
          placeholder={text('addTitle')}
          aria-label={text('titleLabel')}
          className="min-w-0 flex-1 border-b-2 border-gray-200 bg-transparent pb-1.5 ft-lg text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-yellow-500"
        />
        <button type="button" aria-label={text('close')} onClick={onClose} className="rounded-md p-1 hover:bg-gray-100">
          <CrossIcon wh={18} color="#374151" />
        </button>
      </div>
      <div className="mt-3">
        <ChoiceChips
          options={(draft.allDay ? ['todo'] : TYPES).map((type) => [type, text(`type_${type}`)])}
          value={draft.type}
          onPick={(type) => onChange({ type })}
        />
      </div>
      {meeting ? (
        <div className="mt-3 flex flex-col gap-2 text-sm text-gray-700">
          {(colleagues.length > 0 || ownerId) && (
            <div className="flex items-center gap-2">
              <span className="shrink-0">{text('calendarOf')}</span>
              <select
                aria-label={text('calendarOf')}
                value={ownerId ?? ''}
                onChange={(event) => onChange({ ownerId: event.target.value || null })}
                className={fieldClass}
              >
                <option value="">{fullName(me)}</option>
                {ownerId && !owner && <option value={ownerId} />}
                {colleagues.map((person) => (
                  <option key={person.id} value={person.id}>
                    {fullName(person)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="checkbox-primary"
              checked={wholeDay}
              onChange={(event) => onChange({ meetingAllDay: event.target.checked })}
            />
            {text('allDayLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input type="date" aria-label={text('startDayLabel')} value={fromDay} onChange={(event) => moveFromDay(event.target.value)} className={fieldClass} />
            {!wholeDay && (
              <input type="time" aria-label={text('startTimeLabel')} value={fromTime} onChange={(event) => onChange({ fromTime: event.target.value })} className={fieldClass} />
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label={text('endDayLabel')}
              value={toDay}
              min={fromDay}
              onChange={(event) => onChange({ toDay: event.target.value })}
              className={fieldClass}
            />
            {!wholeDay && (
              <input type="time" aria-label={text('endTimeLabel')} value={toTime} onChange={(event) => onChange({ toTime: event.target.value })} className={fieldClass} />
            )}
          </div>
        </div>
      ) : (
        <p className="mt-3 flex items-center gap-2 text-sm text-gray-700">
          <span className="flex shrink-0 text-gray-400">
            <ClockIcon wh={16} color="currentColor" />
          </span>
          {longDay(draft.start)} · {draft.allDay ? text('noTime') : `${clockLabel(draft.start)} – ${clockLabel(end)}`}
        </p>
      )}
      <textarea
        value={draft.note ?? ''}
        onChange={(event) => onChange({ note: event.target.value })}
        onKeyDown={(event) => (event.metaKey || event.ctrlKey) && event.key === 'Enter' && save()}
        rows={2}
        placeholder={text('addDescription')}
        aria-label={text('descriptionLabel')}
        className="mt-3 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm leading-5 text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-yellow-500"
      />
      <div className="mt-4 flex justify-end gap-2">
        {draft.type !== 'meeting' && (
          <Button color="SECONDARY" size="SM" onClick={onMore}>
            {text('moreOptions')}
          </Button>
        )}
        <Button color="PRIMARY" size="SM" disabled={!title || busy || (meeting && !slotOk)} onClick={save}>
          {busy ? text('saving') : text('save')}
        </Button>
      </div>
    </div>
  );
};

export default QuickCreate;
